import React, { useCallback, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import Translator from '../../../components/helpers/Translator';
import { axiosPath } from '../../../../routing/helpers/router';
import minions from '../../../../minions/minions';
import useIsMounted from '../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';

const ModuleComsaWasteManagementReportManagers = (props) => {
  const {
    reportOptions,
    codeType,
    codes,
    onUpdateReports,
    onUpdateManagers,
  } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const currentPage = useSelector((state) => state.currentPage);
  const [showListModal, setShowListModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [initialValues, setInitialValues] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const dynamicAreaButton = useRef(null);

  const closeModals = useCallback(() => {
    setDeleteId(undefined);
    setShowListModal(false);
    setShowAddModal(false);
    setShowDeleteModal(false);
  }, []);

  // < ADD/EDIT MANAGER
  const handleAddModal = useCallback(() => {
    closeModals();
    setShowAddModal(true);
    setInitialValues({
      name: '',
      cif: '',
      town: '',
      dynamicArea: [
        {
          code: null,
          wasteManagement: null,
          operation: null,
          valuablePercentage: '',
          intermediateManager: '',
        },
      ],
      deletedFiles: [],
      password: '',
    });
  }, []);

  const handleEditModal = useCallback((m) => {
    closeModals();
    setShowAddModal(true);
    setInitialValues({
      id: m.id,
      name: m.name,
      cif: m.cif,
      town: m.town,
      dynamicArea: m.codes.map((mc) => ({
        code: mc.codeId,
        wasteManagement: mc.wasteManagement,
        operation: mc.operation,
        valuablePercentage: mc.valuablePercentage,
        intermediateManager: mc.intermediateManager,
      })),
      files: m.files,
      deletedFiles: [],
      password: '',
    });
  }, []);

  const addManager = useCallback(
    async (payload) => {
      await axios
        .post(
          axiosPath('api_r_user_module_comsaReports_manager_edit'),
          minions.forms.toFormData({
            ...payload,
            type: 'wasteManagementManager',
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        .then((r) => {
          if (isMounted.current) {
            closeModals();
            onUpdateManagers();
            onUpdateReports();
            setShowListModal(true);
            if (r.data.isFileUploadedError) {
              AppNotificationAlert({
                title: r.data.notification.title,
                message: r.data.notification.message,
                type: 'danger',
                duration: 5000,
              });
            }
          }
        })
        .catch((e) => {
          if (e?.response?.status === 428) {
            AppNotificationAlert({
              title: 'Error',
              message: 'error',
              type: 'danger',
              duration: 5000,
            });
          }
        });
    },
    [deleteId, moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < ADD MANAGER

  const removeDynamicArea = useCallback((dynamicArea, arrayHelpers, i) => {
    if (dynamicArea.length > 1) arrayHelpers.remove(i);
  }, []);

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        'deletedFiles',
        deletedFiles.filter((s) => s !== salt)
      );
    } else setFieldValue('deletedFiles', [...deletedFiles, salt]);
  };

  // < DELETE MANAGER
  const deleteManager = useCallback(
    async (payload) => {
      await axios
        .post(
          axiosPath('api_r_user_module_comsaReports_manager_delete'),
          {
            id: deleteId,
            password: payload.password,
            type: 'wasteManagementManager',
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }
        )
        .then(() => {
          if (isMounted.current) {
            closeModals();
            onUpdateManagers();
            onUpdateReports();
            setShowListModal(true);
          }
        })
        .catch((e) => {
          if (e?.response?.status === 428) {
            AppNotificationAlert({
              title: 'Error',
              message: 'error',
              type: 'danger',
              duration: 5000,
            });
          }
        });
    },
    [deleteId, moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < DELETE MANAGER

  // < FORM SCHEMAS
  const addValidationSchema = Yup.object().shape({
    name: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
    cif: Yup.string().required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    town: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
    dynamicArea: Yup.array().of(
      Yup.object().shape({
        code: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        wasteManagement: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        operation: Yup.number()
          .nullable()
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
        valuablePercentage: Yup.string()
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      })
    ),
    password: Yup.string().required(
      <Translator file="user" name="user.required" />
    ),
  });

  const deleteValidationSchema = Yup.object().shape({
    password: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });
  // < FORM SCHEMAS

  const getManagerFile = (managerID, salt) => axios
      .post(
          axiosPath('api_r_user_module_comsaReports_manager_file_get'),
          {
            moduleBridgeHex,
            managerID,
            salt,
          }
      )
      .then(({ data }) => (window.location = data))
      .catch((e) => dispatch(errorHandler(e)))

  return (
    <>
      <span
        className="btn btn-sm btn-outline-info"
        onClick={() => setShowListModal(true)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="managers" />
        )}
      >
        <AppRemixIcon icon="user-settings" />
      </span>
      <FullScreenModal
        show={showListModal}
        size="xl"
        title={<Translator file="module-comsaReports" name="managers" />}
        onClose={() => setShowListModal(false)}
      >
        <div className="row mb-3">
          <div className="col-12">
            <span
              className="btn btn-success btn-sm mr-2"
              onClick={handleAddModal}
              title={ReactDOMServer.renderToString(
                <Translator file="module-comsaReports" name="add.manager" />
              )}
            >
              <AppRemixIcon icon="add" />
            </span>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <Translator file="module-comsaReports" name="name" />
                    </th>
                    <th>
                      <Translator file="module-comsaReports" name="cif" />
                    </th>
                    <th>
                      <Translator file="module-comsaReports" name="town" />
                    </th>
                    <th>
                      <Translator file="module-comsaReports" name="code" /> (
                      {codeType})
                    </th>
                    <th>
                      <Translator
                        file="module-comsaReports"
                        name="denomination"
                      />
                    </th>
                    <th>
                      <Translator
                        file="module-comsaReports"
                        name="waste.management.single"
                      />
                    </th>
                    <th>
                      <Translator file="module-comsaReports" name="operation" />
                    </th>
                    <th>
                      <Translator
                        file="module-comsaReports"
                        name="valuable.material.percentage"
                      />
                    </th>
                    <th>
                      <Translator
                        file="module-comsaReports"
                        name="intermediate.manager"
                      />
                    </th>
                    <th>
                      <Translator
                        file="module-comsaReports"
                        name="certificate"
                      />
                    </th>
                    <th
                      style={{
                        minWidth: '113px',
                        width: '113px',
                      }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {currentPage?.options?.managers?.map((m) => (
                    <React.Fragment key={`manager-${m.id}`}>
                      <tr>
                        <td>{m.name}</td>
                        <td>{m.cif}</td>
                        <td>{m.town}</td>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td>
                          {m.files &&
                            Object.keys(m.files).map((salt, index) => (
                                <span
                                    key={index}
                                    className="mr-1 pointer"
                                    onClick={() => getManagerFile(m.id, salt)}
                                >
                                    <AppRemixIcon icon="file-text" />{' '}
                                    {m.files[salt].name}
                                </span>
                            ))}
                        </td>
                        <td className="text-center">
                          <span
                            className="btn btn-info btn-xs mr-1"
                            onClick={() => handleEditModal(m)}
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-comsaReports"
                                name="edit"
                              />
                            )}
                          >
                            <AppRemixIcon icon="edit" />
                          </span>
                          <span
                            className="btn btn-danger btn-xs"
                            onClick={() => {
                              closeModals();
                              setShowDeleteModal(true);
                              setDeleteId(m.id);
                            }}
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-comsaReports"
                                name="delete"
                              />
                            )}
                          >
                            <AppRemixIcon icon="delete-bin-6" />
                          </span>
                        </td>
                      </tr>
                      {m.codes.map((mc) => (
                        <tr key={`managerCode-${mc.id}`}>
                          <td />
                          <td />
                          <td />
                          <td>
                            {
                              currentPage.moduleComsaWasteManagementReportCodes?.find(
                                (c) => c.id === mc.codeId
                              )?.code
                            }
                          </td>
                          <td>
                            {
                              currentPage.moduleComsaWasteManagementReportCodes?.find(
                                (c) => c.id === mc.codeId
                              )?.name
                            }
                          </td>
                          <td>
                            {
                              reportOptions.wasteManagement?.find(
                                (o) => o.value === mc.wasteManagement
                              )?.label
                            }
                          </td>
                          <td>
                            {reportOptions.eliminationOperation?.find(
                              (o) => o.value === mc.operation
                            )?.label ||
                              reportOptions.valuationOperation?.find(
                                (o) => o.value === mc.operation
                              )?.label}
                          </td>
                          <td>
                            {minions.int.renderCommaFloat(
                              mc.valuablePercentage
                            )}
                          </td>
                          <td>
                            {mc.intermediateManager}
                          </td>
                          <td />
                          <td />
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </FullScreenModal>
      <AppModal
        show={showAddModal}
        size="xl"
        title={<Translator file="module-comsaReports" name="manager" />}
        onClose={() => {
          closeModals();
          setShowListModal(true);
        }}
      >
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={addValidationSchema}
          onSubmit={addManager}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-12">
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="name" />
                    )}
                    icon={<AppRemixIcon icon="user" />}
                    value={values?.name}
                    onChange={(v) => setFieldValue('name', v)}
                    error={errors?.name}
                    required
                    isClearable
                  />
                </div>
                <div className="col-md-6">
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="cif" />
                    )}
                    icon={<AppRemixIcon icon="passport" />}
                    value={values?.cif}
                    onChange={(v) => setFieldValue('cif', v)}
                    error={errors?.cif}
                    required
                    isClearable
                  />
                </div>
                <div className="col-md-6">
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="town" />
                    )}
                    icon={<AppRemixIcon icon="building" />}
                    value={values?.town}
                    onChange={(v) => setFieldValue('town', v)}
                    error={errors?.town}
                    required
                    isClearable
                  />
                </div>
                <div className="col-lg-12">
                  <FieldArray
                    name="dynamicArea"
                    render={(arrayHelpers) => (
                      <>
                        {values?.dynamicArea.map((da, daIndex) => (
                          <div
                            key={daIndex}
                            className="row pt-2 pb-2"
                            style={{ borderBottom: '1px solid #f4f6f9' }}
                          >
                            <div className="col-lg-12">
                              <button
                                type="button"
                                className="btn btn-danger btn-xs float-right"
                                onClick={() =>
                                  removeDynamicArea(
                                    values.dynamicArea,
                                    arrayHelpers,
                                    daIndex
                                  )
                                }
                                disabled={values.dynamicArea.length === 1}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="delete"
                                  />
                                )}
                              >
                                <AppRemixIcon icon="delete-bin-6" />
                              </button>
                            </div>
                            <div className="col-md-6">
                              <AppSingleSelect
                                icon={<AppRemixIcon icon="barcode" />}
                                options={codes}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-comsaReports"
                                    name="code"
                                  />
                                )}
                                value={values?.dynamicArea[daIndex]?.code}
                                onChange={(opt) =>
                                  setFieldValue(
                                    `dynamicArea[${daIndex}].code`,
                                    opt
                                  )
                                }
                                isLoading={!codes}
                                hasSearchBox
                                hasSelectOptions
                                error={
                                  errors?.dynamicArea &&
                                  errors?.dynamicArea[daIndex]?.code
                                }
                              />
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppSingleSelect
                                  options={reportOptions.wasteManagement}
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].wasteManagement`,
                                      opt
                                    )
                                  }
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="waste.management.single"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="delete-bin-6" />}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="select.a.waste.management"
                                    />
                                  )}
                                  value={
                                    values?.dynamicArea[daIndex]
                                      ?.wasteManagement
                                  }
                                  error={
                                    errors?.dynamicArea &&
                                    errors?.dynamicArea[daIndex]
                                      ?.wasteManagement
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppSingleSelect
                                  options={
                                    !values.dynamicArea[daIndex].wasteManagement
                                      ? []
                                      : values.dynamicArea[daIndex]
                                          .wasteManagement === 23
                                      ? reportOptions.eliminationOperation
                                      : reportOptions.valuationOperation
                                  }
                                  onChange={(opt) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].operation`,
                                      opt
                                    )
                                  }
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="operation"
                                    />
                                  }
                                  icon={
                                    <AppRemixIcon icon="arrow-left-right" />
                                  }
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="select.an.operation"
                                    />
                                  )}
                                  value={
                                    values?.dynamicArea[daIndex]?.operation
                                  }
                                  error={
                                    errors?.dynamicArea &&
                                    errors?.dynamicArea[daIndex]?.operation
                                  }
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppInputNumber
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="valuable.material.percentage"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="percent" />}
                                  value={
                                    values?.dynamicArea[daIndex]
                                      ?.valuablePercentage
                                  }
                                  onChange={(value) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].valuablePercentage`,
                                      value
                                    )
                                  }
                                  error={
                                    errors?.dynamicArea &&
                                    errors?.dynamicArea[daIndex]
                                      ?.valuablePercentage
                                  }
                                  decimals={3}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <AppInput
                                  title={
                                    <Translator
                                      file="module-comsaReports"
                                      name="intermediate.manager"
                                    />
                                  }
                                  icon={<AppRemixIcon icon="user" />}
                                  value={
                                    values?.dynamicArea[daIndex]
                                      ?.intermediateManager
                                  }
                                  onChange={(value) =>
                                    setFieldValue(
                                      `dynamicArea[${daIndex}].intermediateManager`,
                                      value
                                    )
                                  }
                                  error={
                                    errors?.dynamicArea &&
                                    errors?.dynamicArea[daIndex]
                                      ?.intermediateManager
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="row mt-3">
                          <div className="col-sm-12">
                            <span
                              className="pointer"
                              onClick={() =>
                                arrayHelpers.push({
                                  code: null,
                                  wasteManagement: null,
                                  operation: null,
                                  valuablePercentage: '',
                                  intermediateManager: '',
                                })
                              }
                              ref={dynamicAreaButton}
                            >
                              <AppRemixIcon icon="add-box" />{' '}
                              <Translator
                                file="module-comsaReports"
                                name="add.more.types"
                              />
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  />
                </div>
                <div className="col-12 mt-3 mb-3">
                  <label>
                    <Translator
                      file="module-comsaReports"
                      name="attach.certificate"
                    />
                  </label>
                  <AppFileUploader
                    isMulti
                    placeholder={
                      !values.files || Object.keys(values?.files).length === 0
                        ? ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="select.one.multiple.files"
                            />
                          )
                        : ReactDOMServer.renderToString(
                            <Translator
                              file="general"
                              name="select.number.of.elements"
                              variables={{
                                elements: Object.keys(values?.files).length,
                              }}
                            />
                          )
                    }
                    onChange={(opt) => setFieldValue('files', opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                </div>
                <div className="col-12">
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="password" />
                    )}
                    icon={<AppRemixIcon icon="lock" />}
                    value={values?.password}
                    onChange={(v) => setFieldValue('password', v)}
                    error={errors?.password}
                    required
                    isClearable
                    isPassword
                  />
                </div>
              </div>
              {values.id &&
                Object.keys(
                  currentPage?.options?.managers.find((m) => m.id === values.id)
                    .files || []
                ).length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <label>
                        <Translator file="module-comsaReports" name="delete" />
                      </label>
                      <div>
                        {Object.keys(values.files).map((salt, index) => (
                          <div key={index}>
                            <label className="mb-2 pointer font-weight-light">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={salt}
                                onChange={() =>
                                  handleDeletedFiles(
                                    setFieldValue,
                                    values.deletedFiles,
                                    salt
                                  )
                                }
                                checked={
                                  values.deletedFiles &&
                                  values.deletedFiles[salt]
                                }
                              />
                              {values.files[salt].name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-sm float-right"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <Translator
                      file="module-comsaReports"
                      name={values.id ? 'edit' : 'add'}
                    />
                  </button>
                  <button
                    className="btn btn-outline-light btn-sm float-right mr-2"
                    type="button"
                    onClick={() => {
                      closeModals();
                      setShowListModal(true);
                    }}
                  >
                    <Translator file="module-comsaReports" name="cancel" />
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
      <AppModal
        show={!!deleteId && showDeleteModal}
        title={<Translator file="module-comsaReports" name="delete" />}
        onClose={() => {
          closeModals();
          setShowListModal(true);
        }}
      >
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
          initialValues={{
            password: '',
          }}
          validationSchema={deleteValidationSchema}
          onSubmit={deleteManager}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12">
                  <AppInput
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="password" />
                    )}
                    icon={<AppRemixIcon icon="lock" />}
                    value={values?.password}
                    onChange={(v) => setFieldValue('password', v)}
                    error={errors?.password}
                    required
                    isClearable
                    isPassword
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-danger btn-sm float-right"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    <Translator file="module-comsaReports" name="delete" />
                  </button>
                  <button
                    className="btn btn-outline-light btn-sm float-right mr-2"
                    type="button"
                    onClick={() => {
                      closeModals();
                      setShowListModal(true);
                    }}
                  >
                    <Translator file="module-comsaReports" name="cancel" />
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
};

export default ModuleComsaWasteManagementReportManagers;

const FullScreenModal = styled(AppModal)`
  .modal-dialog {
    max-width: 100% !important;
    margin-left: 10px !important;
    margin-right: 10px !important;
  }
`;
